import { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';

/**
 * Hook to fetch the top10 models for a given priority setting
 * Returns both the model IDs and their creator prefixes for filtering
 */
export function useTop10Models(prioritySetting?: 'free' | 'speed' | 'balance' | 'value' | 'intelligence' | 'custom') {
  const [top10ModelIds, setTop10ModelIds] = useState<Set<string>>(new Set());
  const [top10Creators, setTop10Creators] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // If no priority setting or custom, don't filter (show all)
    if (!prioritySetting || prioritySetting === 'custom') {
      setTop10ModelIds(new Set());
      setTop10Creators(new Set());
      return;
    }

    const fetchTop10 = async () => {
      setIsLoading(true);
      try {
        // Map priority setting to Firestore field name
        const fieldMap: Record<string, string> = {
          'free': 'freeUnified',
          'speed': 'speedy',
          'balance': 'balanced',
          'value': 'value',
          'intelligence': 'top'
        };

        const fieldName = fieldMap[prioritySetting];
        if (!fieldName) {
          console.warn('⚠️ Unknown priority setting:', prioritySetting);
          setTop10ModelIds(new Set());
          setTop10Creators(new Set());
          return;
        }

        const modelListsRef = doc(db, 'system', 'modelLists');
        const modelListsDoc = await getDoc(modelListsRef);

        if (modelListsDoc.exists()) {
          const allData = modelListsDoc.data();
          const listData = allData?.[fieldName];
          const models = listData?.models || [];

          // Get top 10 models (or all if less than 10)
          const top10 = models.slice(0, Math.min(10, models.length));

          // Extract model IDs and creators
          const modelIds = new Set<string>();
          const creators = new Set<string>();

          top10.forEach((model: any) => {
            if (model.openrouterId) {
              modelIds.add(model.openrouterId);
              
              // Extract creator prefix (everything before first /)
              const creator = model.openrouterId.split('/')[0];
              if (creator) {
                creators.add(creator);
              }
            }
          });

          console.log('📊 Top10 models fetched:', {
            prioritySetting,
            fieldName,
            modelCount: modelIds.size,
            creatorCount: creators.size,
            modelIds: Array.from(modelIds),
            creators: Array.from(creators)
          });

          setTop10ModelIds(modelIds);
          setTop10Creators(creators);
        } else {
          console.warn('⚠️ Model lists document not found');
          setTop10ModelIds(new Set());
          setTop10Creators(new Set());
        }
      } catch (error) {
        console.error('❌ Error fetching top10 models:', error);
        setTop10ModelIds(new Set());
        setTop10Creators(new Set());
      } finally {
        setIsLoading(false);
      }
    };

    fetchTop10();
  }, [prioritySetting]);

  return {
    top10ModelIds,
    top10Creators,
    isLoading,
    // Helper function to check if a model ID matches the top10
    isInTop10: (modelId: string) => {
      // If no filtering (custom or no priority), include everything
      if (!prioritySetting || prioritySetting === 'custom' || top10ModelIds.size === 0) {
        return true;
      }
      return top10ModelIds.has(modelId);
    },
    // Helper function to check if a creator matches the top10
    creatorIsInTop10: (creator: string) => {
      // If no filtering (custom or no priority), include everything
      if (!prioritySetting || prioritySetting === 'custom' || top10Creators.size === 0) {
        return true;
      }
      return top10Creators.has(creator);
    }
  };
}

